import { Fragment, memo, useMemo } from 'react'
import { Typography } from '@mui/material'
import { useStyles } from './ProductDimensions.styles'
import { get, startCase } from 'lodash'
import { isEmpty } from 'lodash'
import clsx from 'clsx'
import {
  Dimension,
  DimensionType,
} from '../../../../modules/privateOffer/types'
import { ShortenedName } from '../../../../common/components/ShortenedName/ShortenedName'
import { priceMapping } from '../utils'

export type ProductDimensionsProps = {
  dimensions: Dimension[]
  pricesArray: string[]
  hasFlexiblePayment?: boolean
  isTypeCPPO?: boolean
}

export const ProductDimensions = memo(
  ({
    dimensions,
    pricesArray,
    isTypeCPPO = false,
    hasFlexiblePayment = false,
  }: ProductDimensionsProps) => {
    const { classes } = useStyles()
    const dimensionsByCategory: undefined | Record<DimensionType, Dimension[]> =
      useMemo(
        () =>
          dimensions?.reduce(
            (acc, dimension) => {
              if (dimension.type) {
                if (acc[dimension.type]) {
                  acc[dimension.type].push(dimension)
                } else {
                  acc[dimension.type] = [dimension]
                }
              }
              return acc
            },
            {} as Record<DimensionType, Dimension[]>
          ),
        [dimensions]
      )

    const statusList: Record<string, number> = {
      standard: 1,
      custom: 2,
      additional_usage: 3,
    }
    const dimensionOrder = useMemo(
      () =>
        dimensionsByCategory
          ? Object.keys(dimensionsByCategory).sort(
              (a, b) => statusList[a] - statusList[b]
            )
          : [],
      [dimensions]
    ) as DimensionType[]

    if (isEmpty(dimensions)) {
      return <Typography className={classes.dataText}>-</Typography>
    }
    return (
      <div className={classes.root}>
        {dimensionsByCategory && !!dimensionOrder?.length
          ? dimensionOrder.map(
              (dimensionType: DimensionType, dimensionTypeIndex: number) => {
                const updatedPricesArrayForData =
                  dimensionType === 'additional_usage'
                    ? [isTypeCPPO ? 'additionalUsagePrice' : 'price']
                    : [...pricesArray]
                const updatedPricesArrayForLabel =
                  dimensionType === 'additional_usage'
                    ? ['price']
                    : hasFlexiblePayment
                      ? ['quantity']
                      : [...pricesArray]
                return (
                  <Fragment key={dimensionType}>
                    <Typography
                      className={clsx(
                        classes.productTitle,
                        dimensionTypeIndex > 0 ? classes.productSection : ''
                      )}
                    >
                      {startCase(dimensionType)}
                    </Typography>
                    <DimensionHeaders
                      pricesArray={updatedPricesArrayForLabel}
                    />
                    {dimensionsByCategory &&
                      dimensionsByCategory[dimensionType]?.map(
                        (dimension, idx) => (
                          <div
                            key={`${dimension.name}${idx}`}
                            className={classes.dimensionContainer}
                          >
                            <div className={classes.dimensionRow}>
                              <Typography
                                className={clsx(
                                  classes.dimensionTitle,
                                  classes.dimensionLabelWidth
                                )}
                              >
                                {dimension?.label && (
                                  <ShortenedName
                                    name={dimension.label}
                                    length={21}
                                  />
                                )}
                              </Typography>
                              <Typography
                                className={clsx(
                                  classes.dimensionTitle,
                                  classes.dimensionLabelWidth
                                )}
                              >
                                {dimension?.name && (
                                  <ShortenedName
                                    name={dimension.name}
                                    length={16}
                                  />
                                )}
                              </Typography>
                              <div className={classes.dimensionPrices}>
                                {hasFlexiblePayment &&
                                dimensionType !== 'additional_usage' ? (
                                  <Typography
                                    className={classes.dimensionTitle}
                                    data-testid={`dimensions-quantity-${dimension.dimensionId}`}
                                  >
                                    {dimension.quantity}
                                  </Typography>
                                ) : (
                                  updatedPricesArrayForData.map(price => (
                                    <Typography
                                      className={clsx(
                                        classes.dimensionTitle,
                                        classes.wordBreak
                                      )}
                                      data-testid={`dimensions-price-${dimension.dimensionId}`}
                                    >
                                      {`${dimension.currency} `}
                                      {get(dimension, `prices[${price}]`, '-')}
                                    </Typography>
                                  ))
                                )}
                              </div>
                            </div>
                            <div>
                              <Typography
                                className={classes.dimensionDescription}
                              >
                                {dimension?.labelDescription && (
                                  <ShortenedName
                                    name={dimension.labelDescription}
                                    length={70}
                                  />
                                )}
                              </Typography>
                            </div>
                          </div>
                        )
                      )}
                  </Fragment>
                )
              }
            )
          : null}
      </div>
    )
  }
)

export type DimensionHeadersProps = {
  pricesArray: string[]
}
export const DimensionHeaders = memo(
  ({ pricesArray }: DimensionHeadersProps) => {
    const { classes } = useStyles()
    const updatedMapping: Record<string, string> = {
      ...priceMapping,
      subscriptionPrice: 'Price',
      price: 'Price',
      quantity: 'Quantity',
    }
    return (
      <>
        <div className={clsx(classes.dimensionRow, classes.dimensionHeaders)}>
          <Typography
            className={clsx(
              classes.dimensionTitle,
              classes.dimensionLabelWidth
            )}
          >
            Label
          </Typography>
          <Typography
            className={clsx(
              classes.dimensionTitle,
              classes.dimensionLabelWidth
            )}
          >
            Name
          </Typography>
          <div className={classes.dimensionPrices}>
            {pricesArray.map(price => (
              <Typography className={clsx(classes.dimensionTitle)}>
                {updatedMapping[price]}
              </Typography>
            ))}
          </div>
        </div>
      </>
    )
  }
)
